import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CardUI } from './CardUI';
import { useGameBoard } from './GameBoardContext';
import { getPossibleCaptures } from '../lib/gameUtils';
import { cn } from '../lib/utils';

export const TableArea: React.FC = () => {
  const { gameState, viewportWidth, capturingCards, hoveredCardId } = useGameBoard();

  const isBeingCaptured = (cardId: string) => {
    if (!capturingCards) return false;
    return capturingCards.cards.some(c => c.id === cardId) || capturingCards.playedCard.id === cardId;
  };

  const getLastPlayedCardCoordinates = () => {
    if (!gameState.lastPlayedCard) return { x: 0, y: 0 };
    const idx = gameState.lastPlayedCard.playerIndex;
    const num = gameState.numPlayers;
    if (idx === 0) return { x: 0, y: 200 };
    if (num === 4) {
      if (idx === 1) return { x: 200, y: 0 }; // Right
      if (idx === 2) return { x: 0, y: -200 }; // Top
      if (idx === 3) return { x: -200, y: 0 }; // Left
    } else if (num === 3) {
      if (idx === 1) return { x: 0, y: -200 }; // Top
      if (idx === 2) return { x: -200, y: 0 }; // Left
    } else {
      if (idx === 1) return { x: 0, y: -200 }; // Top
    }
    return { x: 0, y: 0 };
  };

  return (
    <div className="relative flex items-center justify-center z-10 overflow-hidden w-full max-w-[calc(100vw-24px)] mx-auto" style={{ paddingTop: "1rem", paddingBottom: "12rem" }}>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <span className="suit-watermark" style={{ top: '5%', left: '8%' }}>♦</span>
        <span className="suit-watermark" style={{ top: '5%', right: '8%' }}>♥</span>
        <span className="suit-watermark" style={{ bottom: '5%', left: '8%' }}>♠</span>
        <span className="suit-watermark" style={{ bottom: '5%', right: '8%' }}>♣</span>
      </div>
      {/* Decorative circle ring */}
      <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
        <div className="w-[450px] h-[450px] border-4 border-white/20 rounded-full" />
      </div>
      <div
        className="h-full flex items-center justify-center table-felt rounded-3xl"
        style={{
          padding: '0 0.5rem',
          height: 'calc(100vh - 355px)',
          width: `${Math.min(viewportWidth * 0.88, 360) + 24}px`,
          flexShrink: 0,
        }}
      >
        {/* Flex-wrap: rows are left-aligned (content-start), block is centered via justify-center on parent */}
        <div
          className="flex flex-wrap content-start justify-center"
          style={{
            gap: '0.375rem',
            maxWidth: `${Math.min(viewportWidth * 0.88, 360)}px`,
            minHeight: '120px',
          }}
        >
          {(() => {
            const hoveredCard = hoveredCardId ? gameState.players[0].hand.find(c => c.id === hoveredCardId) : null;
            const captureableCardIds = new Set<string>();
            if (hoveredCard) {
              const captures = getPossibleCaptures(hoveredCard, gameState.table);
              captures.forEach(combo => {
                combo.forEach(card => captureableCardIds.add(card.id));
              });
            }

            return (
              <AnimatePresence mode="popLayout">
                {gameState.table
                  .filter(card => !gameState.lastPlayedCard || gameState.lastPlayedCard.card.id !== card.id)
                  .filter(card => !isBeingCaptured(card.id))
                  .map((card) => {
                    const isCaptureable = captureableCardIds.has(card.id);
                    return (
                      <motion.div
                        key={card.id}
                        layoutId={card.id}
                        initial={{ scale: 0, y: -30 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={
                          isBeingCaptured(card.id)
                            ? { opacity: 0, scale: 0.8, transition: { duration: 0.1 } }
                            : { scale: 0.5, y: -60, opacity: 0, transition: { duration: 0.25 } }
                        }
                      >
                        <CardUI
                          card={card}
                          disabled
                          scale={0.8}
                          className={cn(
                            "transition-all duration-300",
                            isCaptureable && "shadow-[0_0_15px_rgba(234,179,8,0.85)] scale-[1.03]"
                          )}
                        />
                      </motion.div>
                    );
                  })}
              </AnimatePresence>
            );
          })()}

          {/* Played Card — rendered in grid alongside table cards, z-50 above all */}
          <AnimatePresence>
            {gameState.lastPlayedCard && !capturingCards && (
              <motion.div
                initial={{
                  ...getLastPlayedCardCoordinates(),
                  scale: 1,
                  opacity: 0
                }}
                animate={{
                  x: 0,
                  y: 0,
                  scale: 1.15,
                  opacity: 1
                }}
                exit={{
                  scale: 0.8,
                  opacity: 0,
                  transition: { duration: 0.1 }
                }}
                className="z-50 pointer-events-none relative"
              >
                <CardUI card={gameState.lastPlayedCard.card} disabled />
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/80 px-2.5 py-0.5 rounded-full text-[9px] text-yellow-400 font-black uppercase tracking-widest border border-white/10 flex items-center gap-1 shadow-lg shadow-black/40 backdrop-blur-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse shrink-0" />
                  {gameState.lastPlayedCard.playerIndex === 0 ? 'Stai giocando...' : `${gameState.players[gameState.lastPlayedCard.playerIndex]?.name || 'Avversario'} sta giocando...`}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {gameState.table.length === 0 && !gameState.lastPlayedCard && (
            <div className="col-span-8 text-white/[0.06] font-black tracking-[0.3em] uppercase text-4xl select-none" style={{ fontFamily: '"Playfair Display", serif' }}>
              SCOPA
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
